import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { CitationNodeView } from './citation-node-view'
import { nanoid } from 'nanoid'

export interface CitationAttributes {
  id?: string
  title?: string
  slug?: string
  topicId?: string
  docId?: string
  sourceType?: string
  updatedAt?: string
  isBrokenLink?: boolean
}

export const CitationSpec = {
  group: 'inline',
  inline: true,
  content: 'text*',
  draggable: false,
  attrs: {
    key: { default: '' },
    label: { default: '' },
    url: { default: '' },
    id: { default: nanoid(10) },
  },
  toDOM() { return ['span', { 'data-citation': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'span[data-citation]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    citation: {
      /**
       * Insert a citation
       */
      insertCitation: (attributes?: CitationAttributes) => ReturnType
      /**
       * Update citation attributes
       */
      updateCitation: (attributes: Partial<CitationAttributes>) => ReturnType
    }
  }
}

export const CitationExtension = Node.create({
  name: 'Citation',

  group: 'inline',

  inline: true,

  content: 'text*',

  draggable: false,

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: element => element.getAttribute('data-citation-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-citation-id': attributes.id,
          }
        },
      },
      title: {
        default: '',
        parseHTML: element => element.getAttribute('title'),
        renderHTML: attributes => {
          if (!attributes.title) {
            return {}
          }
          return {
            'title': attributes.title,
          }
        },
      },
      topicId: {
        default: '',
        parseHTML: element => element.getAttribute('data-topic-id'),
        renderHTML: attributes => {
          if (!attributes.topicId) return {}
          return { 'data-topic-id': attributes.topicId }
        }
      },
      docId: {
        default: '',
        parseHTML: element => element.getAttribute('data-doc-id'),
        renderHTML: attributes => {
          if (!attributes.docId) return {}
          return { 'data-doc-id': attributes.docId }
        }
      },
      slug: {
        default: '',
        parseHTML: element => element.getAttribute('data-slug'),
        renderHTML: attributes => {
          if (!attributes.slug) {
            return {}
          }
          return {
            'data-slug': attributes.slug,
          }
        },
      },
      sourceType: {
        default: '',
        parseHTML: element => element.getAttribute('data-source-type'),
        renderHTML: attributes => {
          if (!attributes.sourceType) {
            return {}
          }
          return {
            'data-source-type': attributes.sourceType,
          }
        },
      },
      updatedAt: {
        default: '',
        parseHTML: element => element.getAttribute('data-updated-at'),
        renderHTML: attributes => {
          if (!attributes.updatedAt) {
            return {}
          }
          return {
            'data-updated-at': attributes.updatedAt,
          }
        },
      },
      isBrokenLink: {
        default: false,
        parseHTML: element => element.getAttribute('data-broken-link') === 'true',
        renderHTML: attributes => {
          if (attributes.isBrokenLink === undefined) {
            return {}
          }
          return {
            'data-broken-link': attributes.isBrokenLink,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="Citation"]',
      },
      // Removed parsing of <a> tags to avoid confusion with links
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',  // Changed from 'a' to 'span' - Citation is not a link but a reference node
      mergeAttributes(HTMLAttributes, { 
        'data-type': 'Citation',
        'class': 'citation-node'  // Use semantic class name
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CitationNodeView)
  },

  addCommands() {
    return {
      insertCitation:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              id: '',
              title: '',
              slug: '',
              sourceType: '',
              updatedAt: '',
              isBrokenLink: false,
              ...attributes,
            },
            content: [
              {
                type: 'text',
                text: attributes.title || 'Citation',
              },
            ],
          })
        },
      updateCitation:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },
}) 